import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { invalidateTaskQueries, getDisplayName } from "@/lib/task-query-utils";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { useLocation, useSearch } from "wouter";
import { useSidebar } from "@/components/ui/sidebar";
import { useBreadcrumbs } from "@/contexts/breadcrumb-context";
import { PageHeader } from "@/components/page-header";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchEmptyState } from "@/components/ui/search-empty-state";
import { PaginationControls } from "@/components/ui/pagination-controls";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarSrc } from "@/components/user-avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent } from "@/components/ui/card";
import {
  Plus,
  MoreHorizontal,
  Archive,
  UserCircle,
  Search,
  CheckSquare,
  MessageSquare,
  Paperclip,
  PawPrint,
  Link2,
  X,
  Kanban,
  List,
  History,
  Eye,
  EyeOff,
  ClipboardList,
  Settings,
  Copy,
} from "lucide-react";
import { CollapsibleFilterHeader } from "@/components/ui/collapsible-filter-header";
import { CreateTaskSheet } from "@/components/create-task-sheet";
import { TemplatePicker } from "@/components/tasks/template-picker";
import { TaskKanbanView } from "@/components/task-kanban-view";
import { LinkedAnimalsTooltip } from "@/components/tasks/linked-animals-tooltip";
import { MyTasksWidget } from "@/components/my-tasks-widget";
import { TaskPreviewPanel } from "@/components/tasks/task-preview-panel";
import { PriorityBadge } from "@/components/tasks/priority-badge";
import { MyBoardsGrid, type BoardSummary } from "@/components/tasks/my-boards-grid";
import { ActivityFeed, type ActivityRecord } from "@/components/tasks/activity-feed";
import { Separator } from "@/components/ui/separator";

interface TaskListItem {
  id: string;
  boardId: string;
  title: string;
  description: string | null;
  statusId: string;
  rank: number;
  assigneeUserId: string | null;
  dueDate: string | null;
  priority: string | null;
  createdAt: string;
  updatedAt: string;
  statusName: string;
  statusSortOrder: number;
  assigneeUsername: string | null;
  assigneeFirstName: string | null;
  assigneeLastName: string | null;
  assigneeAvatarUrl: string | null;
  animalCount: number;
  firstAnimalName: string | null;
  interactionCount: number;
  checklist: { done: number; total: number };
  commentCount: number;
  attachmentCount?: number;
  labels: Array<{ labelId: string; labelName: string; labelColor: string | null }>;
}

interface TasksResponse {
  tasks: TaskListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

interface Board {
  id: string;
  name: string;
  type?: string;
  isArchived: boolean;
}

interface Status {
  id: string;
  name: string;
  isArchived: boolean;
  sortOrder: number;
}

interface BoardLabel {
  id: string;
  name: string;
  color: string | null;
  isArchived: boolean;
}

interface BoardMember {
  userId: string;
  role: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
}

function isOverdue(dueDate: string | null): boolean {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dueDate) < today;
}

function getInitials(name: string | null, username: string | null): string {
  if (name) return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  if (username) return username.slice(0, 2).toUpperCase();
  return "?";
}

export default function TasksPage() {
  usePageTitle("Tasks");
  const { user } = useAuth();
  const { toast } = useToast();
  const { isMobile } = useSidebar();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const urlParams = useMemo(() => new URLSearchParams(searchString), [searchString]);
  const selectedBoardId = urlParams.get("boardId") || "";
  const selectedTaskId = urlParams.get("taskId") || "";
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [labelFilter, setLabelFilter] = useState<string>("all");
  const [noAnimalsFilter, setNoAnimalsFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch((prev) => {
        if (prev !== searchQuery) setPage(1);
        return searchQuery;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  const [pageSize, setPageSize] = useState(50);
  const [createOpen, setCreateOpen] = useState(false);
  const [createStatusId, setCreateStatusId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filterCollapsed, setFilterCollapsed] = useState<boolean>(() => window.innerWidth < 640);
  const [viewMode, setViewMode] = useState<"list" | "kanban" | "activity">("list");
  const [activityPage, setActivityPage] = useState(1);
  const [activityPageSize, setActivityPageSize] = useState(25);
  const [showAllActivity, setShowAllActivity] = useState(false);

  const { data: boardsData, isLoading: boardsLoading } = useQuery<{ boards: Board[] }>({
    queryKey: ["/api/task-boards", "memberOnly"],
    queryFn: async () => {
      const res = await fetch("/api/task-boards?memberOnly=true", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch boards");
      return res.json();
    },
  });

  const { data: summariesData, isLoading: summariesLoading } = useQuery<{ boards: BoardSummary[] }>({
    queryKey: ["/api/task-boards/summaries?type=regular"],
  });

  const boards = useMemo(() =>
    (boardsData?.boards ?? []).filter(b => !b.isArchived && (!b.type || b.type === "regular")),
    [boardsData],
  );

  const currentBoard = boards.find(b => b.id === selectedBoardId);

  useBreadcrumbs(
    currentBoard
      ? [
          { label: "Planning" },
          { label: "Tasks", href: "/tasks", onClick: (e: React.MouseEvent) => { e.preventDefault(); handleBoardChange(""); } },
          { label: currentBoard.name },
        ]
      : [{ label: "Planning" }, { label: "Tasks" }]
  );

  const { data: statusesData } = useQuery<{ statuses: Status[] }>({
    queryKey: ["/api/task-boards", selectedBoardId, "statuses"],
    enabled: !!selectedBoardId,
  });

  const { data: labelsData } = useQuery<{ labels: BoardLabel[] }>({
    queryKey: ["/api/task-boards", selectedBoardId, "labels"],
    enabled: !!selectedBoardId,
  });

  const { data: membersData } = useQuery<{ members: BoardMember[] }>({
    queryKey: ["/api/tasks/board-members", selectedBoardId],
    enabled: !!selectedBoardId,
  });

  const { data: templatesData, isSuccess: templatesLoaded } = useQuery<{ templates: { id: string; title: string; description: string | null; priority: string | null; labelIds: string[] }[] }>({
    queryKey: ["/api/task-boards", selectedBoardId, "templates"],
    enabled: !!selectedBoardId,
  });

  const activeStatuses = useMemo(() =>
    (statusesData?.statuses ?? []).filter(s => !s.isArchived),
    [statusesData],
  );
  const activeLabels = useMemo(() =>
    (labelsData?.labels ?? []).filter(l => !l.isArchived),
    [labelsData],
  );
  const members = membersData?.members ?? [];

  const userRole = useMemo(() => {
    if (user?.isAdmin) return "admin";
    const member = members.find(m => m.userId === user?.id);
    return member?.role ?? null;
  }, [user, members]);

  const canEdit = userRole === "admin" || userRole === "editor";

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("boardId", selectedBoardId);
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (assigneeFilter !== "all") params.set("assignee", assigneeFilter);
    if (labelFilter !== "all") params.set("label", labelFilter);
    if (noAnimalsFilter) params.set("noAnimals", "true");
    if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
    return params.toString();
  }, [selectedBoardId, page, pageSize, statusFilter, assigneeFilter, labelFilter, noAnimalsFilter, debouncedSearch]);

  const kanbanQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("boardId", selectedBoardId);
    params.set("page", "1");
    params.set("pageSize", "500");
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (assigneeFilter !== "all") params.set("assignee", assigneeFilter);
    if (labelFilter !== "all") params.set("label", labelFilter);
    if (noAnimalsFilter) params.set("noAnimals", "true");
    if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
    return params.toString();
  }, [selectedBoardId, statusFilter, assigneeFilter, labelFilter, noAnimalsFilter, debouncedSearch]);

  const { data: tasksData, isLoading: tasksLoading } = useQuery<TasksResponse>({
    queryKey: ["/api/tasks?" + queryParams],
    enabled: !!selectedBoardId && viewMode === "list",
  });

  const { data: kanbanTasksData, isLoading: kanbanLoading } = useQuery<TasksResponse>({
    queryKey: ["/api/tasks?" + kanbanQueryParams],
    enabled: !!selectedBoardId && viewMode === "kanban",
  });

  const activityQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String(activityPage));
    params.set("pageSize", String(activityPageSize));
    if (!showAllActivity) {
      params.set("importance", "high");
    }
    return params.toString();
  }, [activityPage, activityPageSize, showAllActivity]);

  const { data: activityData, isLoading: activityLoading } = useQuery<{ activity: ActivityRecord[]; totalCount: number; totalAllCount: number }>({
    queryKey: [`/api/task-boards/${selectedBoardId}/activity?${activityQueryParams}`],
    enabled: !!selectedBoardId && viewMode === "activity",
  });

  const activityList = activityData?.activity ?? [];
  const activityTotalCount = activityData?.totalCount ?? 0;
  const activityTotalAllCount = activityData?.totalAllCount ?? 0;
  const activityTotalPages = Math.ceil(activityTotalCount / activityPageSize) || 1;

  const tasksList = viewMode === "kanban" ? (kanbanTasksData?.tasks ?? []) : (tasksData?.tasks ?? []);
  const totalCount = viewMode === "kanban" ? (kanbanTasksData?.totalCount ?? 0) : (tasksData?.totalCount ?? 0);
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const isTasksLoading = viewMode === "kanban" ? kanbanLoading : tasksLoading;

  const archiveMutation = useMutation({
    mutationFn: async (taskId: string) => {
      return await apiRequest("PATCH", `/api/tasks/${taskId}/archive`);
    },
    onSuccess: () => {
      invalidateTaskQueries();
      toast({ title: "Task archived" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const changeStatusMutation = useMutation({
    mutationFn: async ({ taskId, statusId }: { taskId: string; statusId: string }) => {
      return await apiRequest("PATCH", `/api/tasks/${taskId}`, { statusId });
    },
    onSuccess: () => {
      invalidateTaskQueries();
      toast({ title: "Status updated" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const changeAssigneeMutation = useMutation({
    mutationFn: async ({ taskId, assigneeUserId }: { taskId: string; assigneeUserId: string | null }) => {
      return await apiRequest("PATCH", `/api/tasks/${taskId}`, { assigneeUserId });
    },
    onSuccess: () => {
      invalidateTaskQueries();
      toast({ title: "Assignee updated" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const duplicateTaskMutation = useMutation({
    mutationFn: async (taskId: string) => {
      return await apiRequest("POST", `/api/tasks/${taskId}/duplicate`);
    },
    onSuccess: () => {
      invalidateTaskQueries();
      toast({ title: "Task duplicated" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  function handleDuplicate(taskId: string) {
    duplicateTaskMutation.mutate(taskId);
  }

  const [createTemplateId, setCreateTemplateId] = useState<string | null>(null);
  const [createTemplateTitle, setCreateTemplateTitle] = useState("");
  const [createTemplateDescription, setCreateTemplateDescription] = useState("");
  const [createTemplatePriority, setCreateTemplatePriority] = useState<string | null>(null);
  const [createTemplateLabelIds, setCreateTemplateLabelIds] = useState<string[]>([]);

  function handleNewTask() {
    if (!templatesLoaded) {
      setPickerOpen(true);
      return;
    }
    const templates = templatesData?.templates ?? [];
    if (templates.length === 0) {
      setCreateTemplateId(null);
      setCreateTemplateTitle("");
      setCreateTemplateDescription("");
      setCreateTemplatePriority(null);
      setCreateTemplateLabelIds([]);
      setCreateOpen(true);
    } else if (templates.length === 1 && currentBoard?.type !== "regular") {
      const t = templates[0];
      setCreateTemplateId(t.id);
      setCreateTemplateTitle(t.title);
      setCreateTemplateDescription(t.description ?? "");
      setCreateTemplatePriority(t.priority);
      setCreateTemplateLabelIds(t.labelIds ?? []);
      setCreateOpen(true);
    } else {
      setPickerOpen(true);
    }
  }

  function handleTemplateSelect(templateId: string | null, _boardId?: string, templateData?: { title: string; description: string | null; priority: string | null; labelIds: string[] }) {
    if (templateId && templateData) {
      setCreateTemplateId(templateId);
      setCreateTemplateTitle(templateData.title);
      setCreateTemplateDescription(templateData.description ?? "");
      setCreateTemplatePriority(templateData.priority);
      setCreateTemplateLabelIds(templateData.labelIds);
    } else {
      setCreateTemplateId(null);
      setCreateTemplateTitle("");
      setCreateTemplateDescription("");
      setCreateTemplatePriority(null);
      setCreateTemplateLabelIds([]);
    }
    setCreateOpen(true);
    setCreateStatusId(null);
  }

  function handleBoardChange(boardId: string) {
    setPage(1);
    setStatusFilter("all");
    setAssigneeFilter("all");
    setLabelFilter("all");
    setNoAnimalsFilter(false);
    setSearchQuery("");
    if (boardId) {
      setLocation("/tasks?boardId=" + boardId);
    } else {
      setLocation("/tasks");
    }
  }

  function handleViewBoardActivity(boardId: string) {
    handleBoardChange(boardId);
    setViewMode("activity");
    setActivityPage(1);
  }

  function clearFilters() {
    setStatusFilter("all");
    setAssigneeFilter("all");
    setLabelFilter("all");
    setNoAnimalsFilter(false);
    setSearchQuery("");
    setDebouncedSearch("");
    setPage(1);
  }

  const hasActiveFilters = statusFilter !== "all" || assigneeFilter !== "all" || labelFilter !== "all" || noAnimalsFilter || debouncedSearch.trim() !== "";

  const filterDescription = useMemo(() => {
    const parts: string[] = [];
    const boardName = currentBoard?.name ?? "this board";
    parts.push(`Tasks on ${boardName}`);
    if (statusFilter !== "all") {
      const st = activeStatuses.find(s => s.id === statusFilter);
      if (st) parts.push(`with status "${st.name}"`);
    }
    if (assigneeFilter !== "all") {
      const m = members.find(m => m.userId === assigneeFilter);
      if (m) parts.push(`assigned to ${getDisplayName(m.firstName, m.lastName, m.username)}`);
    }
    if (labelFilter !== "all") {
      const l = activeLabels.find(l => l.id === labelFilter);
      if (l) parts.push(`labeled "${l.name}"`);
    }
    if (noAnimalsFilter) parts.push("with no linked animals");
    if (debouncedSearch.trim()) parts.push(`matching "${debouncedSearch.trim()}"`);
    return parts.join(" ") + ".";
  }, [selectedBoardId, currentBoard, statusFilter, assigneeFilter, labelFilter, noAnimalsFilter, debouncedSearch, activeStatuses, members, activeLabels]);

  function handleTaskClick(taskId: string) {
    if (isMobile) {
      setLocation(`/tasks/${taskId}`);
    } else {
      const params = new URLSearchParams();
      if (selectedBoardId) params.set("boardId", selectedBoardId);
      params.set("taskId", taskId);
      setLocation(`/tasks?${params.toString()}`);
    }
  }

  function handlePanelClose() {
    if (selectedBoardId) {
      setLocation(`/tasks?boardId=${selectedBoardId}`);
    } else {
      setLocation("/tasks");
    }
  }

  const boardSummaries = summariesData?.boards ?? [];

  function getUserRoleForBoard(boardId: string): string | null {
    if (!user) return null;
    if (user.isAdmin) return "admin";
    const board = boardSummaries.find(b => b.id === boardId);
    if (!board) return null;
    const me = board.members.find(m => m.userId === user.id);
    return me?.role ?? null;
  }

  function handleManageBoard(boardId: string) {
    setLocation(`/admin/tasks?boardId=${boardId}`);
  }

  const currentBoardRole = selectedBoardId ? getUserRoleForBoard(selectedBoardId) : null;
  const isCurrentBoardAdmin = currentBoardRole === "admin";

  if (!user) return null;

  return (
    <div className="flex flex-col h-full overflow-auto" data-testid="page-tasks">
      {selectedBoardId && (
      <div className="sticky top-0 z-40 px-4 sm:px-6 pt-3 pb-3 border-b bg-muted/80 backdrop-blur supports-[backdrop-filter]:bg-muted/60" data-testid="filter-bar">
        <div className="flex items-center gap-3 mb-3">
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2" data-testid="heading-tasks">
            <ClipboardList className="w-6 h-6 text-primary" />
            Tasks
          </h1>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="hidden md:inline text-sm text-muted-foreground whitespace-nowrap shrink-0" data-testid="text-switch-boards-label">Switch Boards:</span>
            <Select value={selectedBoardId} onValueChange={v => handleBoardChange(v === "__none__" ? "" : v)}>
              <SelectTrigger className="flex-1 sm:flex-none sm:w-[180px] text-left" data-testid="select-board">
                <SelectValue placeholder="Select board..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">All Boards</SelectItem>
                {boards.map(b => (
                  <SelectItem key={b.id} value={b.id} data-testid={`option-board-${b.id}`}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {currentBoardRole && (
              <Badge variant="secondary" className="text-[10px] shrink-0 hidden sm:inline-flex" data-testid="badge-current-board-role">
                {currentBoardRole}
              </Badge>
            )}
            {isCurrentBoardAdmin && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 hidden sm:inline-flex"
                    onClick={() => handleManageBoard(selectedBoardId)}
                    data-testid="button-manage-current-board"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Board Settings</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          {canEdit && (
            <>
              <Button
                data-testid="button-create-task"
                onClick={handleNewTask}
                className="shrink-0 hidden sm:inline-flex"
              >
                <Plus className="w-4 h-4 mr-1" />
                New Task
              </Button>
              <Button
                data-testid="button-create-task-mobile"
                size="icon"
                onClick={handleNewTask}
                className="shrink-0 sm:hidden"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
        {viewMode !== "activity" && (
          <Card data-testid="filter-card">
            <CardContent className="p-4">
              <CollapsibleFilterHeader
                collapsed={filterCollapsed}
                onToggle={() => setFilterCollapsed(c => !c)}
                description={filterDescription}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={clearFilters}
              >
                <div className="flex flex-wrap gap-4 mt-3">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        data-testid="input-search"
                        placeholder="Search tasks, comments, checklists..."
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>

                  <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
                    <SelectTrigger className="w-[140px]" data-testid="select-status-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {activeStatuses.map(s => (
                        <SelectItem key={s.id} value={s.id} data-testid={`option-status-${s.id}`}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={assigneeFilter} onValueChange={v => { setAssigneeFilter(v); setPage(1); }}>
                    <SelectTrigger className="w-[140px]" data-testid="select-assignee-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Assignees</SelectItem>
                      {members.map(m => (
                        <SelectItem key={m.userId} value={m.userId} data-testid={`option-assignee-${m.userId}`}>
                          {getDisplayName(m.firstName, m.lastName, m.username)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={labelFilter} onValueChange={v => { setLabelFilter(v); setPage(1); }}>
                    <SelectTrigger className="w-[140px]" data-testid="select-label-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Labels</SelectItem>
                      {activeLabels.map(l => (
                        <SelectItem key={l.id} value={l.id} data-testid={`option-label-${l.id}`}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Button
                    variant={noAnimalsFilter ? "default" : "outline"}
                    size="sm"
                    data-testid="button-no-animals-filter"
                    onClick={() => { setNoAnimalsFilter(!noAnimalsFilter); setPage(1); }}
                  >
                    <PawPrint className="w-3 h-3 mr-1" />
                    No Animals
                  </Button>
                </div>
              </CollapsibleFilterHeader>
            </CardContent>
          </Card>
        )}

        {selectedBoardId && (
          <div className="flex items-center gap-2 mt-3 px-1">
            <span className="text-sm text-muted-foreground shrink-0">View:</span>
            <div className="flex items-center gap-2" data-testid="view-mode-toggle">
              <div className="inline-flex rounded-md border">
                <Button
                  variant={viewMode === "kanban" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("kanban")}
                  className="rounded-r-none"
                  data-testid="button-view-kanban"
                >
                  <Kanban className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className="rounded-l-none"
                  data-testid="button-view-list"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              <Separator orientation="vertical" className="h-6" />
              <Button
                variant={viewMode === "activity" ? "default" : "ghost"}
                size="sm"
                onClick={() => { setViewMode("activity"); setActivityPage(1); }}
                data-testid="button-view-activity"
              >
                <History className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
      )}
      <div className="flex-1 flex overflow-hidden">
        <div className={`flex-1 flex flex-col overflow-auto pb-4 ${selectedTaskId && !isMobile ? "pl-4 sm:pl-6 pr-0" : "px-4 sm:px-6"}`}>
        {boardsLoading ? (
          <div className="space-y-3 pt-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : !selectedBoardId ? (
          <div className="flex-1 pt-4 space-y-6" data-testid="tasks-landing">
            <PageHeader
              title={
                <span className="flex items-center gap-2" data-testid="heading-tasks">
                  <ClipboardList className="w-6 h-6 text-primary" />
                  Tasks
                </span>
              }
            />
            <div>
              <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1" data-testid="heading-my-boards">My Boards</h2>
              <p className="text-xs text-muted-foreground mb-3" data-testid="text-my-boards-description">Boards you have access to. Your permission level is shown on each board.</p>
              <MyBoardsGrid
                boards={summariesData?.boards ?? []}
                isLoading={summariesLoading}
                onSelectBoard={handleBoardChange}
                showActivity={true}
                onViewActivity={handleViewBoardActivity}
                currentUserId={user?.id}
                isCurrentUserAdmin={user?.isAdmin ?? false}
              />
            </div>
            <MyTasksWidget onTaskClick={handleTaskClick} />
          </div>
        ) : viewMode === "activity" ? (
          <div className="flex-1 flex flex-col pt-4" data-testid="task-activity-tab">
            {!activityLoading && (
              <div className="flex flex-wrap items-center gap-2 px-1 mb-3">
                <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">ACTIVITY</span>
                <span className="text-sm text-muted-foreground">—</span>
                <span className="text-sm text-foreground">
                  {showAllActivity
                    ? `${activityTotalCount} event${activityTotalCount !== 1 ? "s" : ""}`
                    : `Viewing ${activityTotalCount} of ${activityTotalAllCount} event${activityTotalAllCount !== 1 ? "s" : ""}`}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setShowAllActivity(!showAllActivity); setActivityPage(1); }}
                  className="text-muted-foreground"
                  data-testid="button-activity-importance-toggle"
                >
                  {showAllActivity ? (
                    <>
                      <EyeOff className="h-3.5 w-3.5 mr-1" />
                      Show less
                    </>
                  ) : (
                    <>
                      <Eye className="h-3.5 w-3.5 mr-1" />
                      View all activity
                    </>
                  )}
                </Button>
              </div>
            )}
            <div className="flex-1 overflow-auto">
              <ActivityFeed
                activity={activityList}
                isLoading={activityLoading}
                showTaskTitle={true}
                onTaskClick={handleTaskClick}
                emptyMessage="No activity on this board yet"
                showAllDetails={showAllActivity}
              />
            </div>
            {!activityLoading && activityTotalCount > 0 && (
              <PaginationControls
                currentPage={activityPage}
                totalPages={activityTotalPages}
                total={activityTotalCount}
                pageSize={activityPageSize}
                startIndex={(activityPage - 1) * activityPageSize + 1}
                endIndex={Math.min(activityPage * activityPageSize, activityTotalCount)}
                onPageChange={setActivityPage}
                onPageSizeChange={size => { setActivityPageSize(size); setActivityPage(1); }}
                itemLabel="events"
                pageSizeOptions={[10, 25, 50, 100]}
                testIdPrefix="activity"
                className="border-t"
              />
            )}
          </div>
        ) : isTasksLoading ? (
          <div className="space-y-3 pt-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : viewMode === "kanban" ? (
          <>
          {!isTasksLoading && (
            <div className="flex flex-wrap items-center gap-2 px-1 pt-3 mb-2">
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{hasActiveFilters ? "RESULTS" : "SHOWING"}</span>
              <span className="text-sm text-muted-foreground">—</span>
              <span className="text-sm text-foreground">{totalCount} task{totalCount !== 1 ? "s" : ""}</span>
            </div>
          )}
          <TaskKanbanView
            tasks={tasksList}
            statuses={activeStatuses}
            members={members}
            labels={activeLabels}
            canEdit={canEdit}
            onTaskClick={handleTaskClick}
            onCreateTask={(statusId) => { setCreateStatusId(statusId ?? null); handleNewTask(); }}
            onDuplicate={handleDuplicate}
          />
          </>
        ) : tasksList.length === 0 ? (
          <div className="flex-1 flex items-center justify-center" data-testid="empty-no-tasks">
            {hasActiveFilters ? (
              <SearchEmptyState />
            ) : (
              <div className="text-center space-y-2">
                <CheckSquare className="w-12 h-12 text-muted-foreground mx-auto" />
                <p className="text-lg font-medium text-foreground">No tasks yet</p>
                <p className="text-sm text-muted-foreground">
                  {canEdit ? "Create your first task to get started." : "No tasks have been created for this board."}
                </p>
                {canEdit && (
                  <Button
                    data-testid="button-create-first-task"
                    onClick={handleNewTask}
                    className="mt-2"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Create Task
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 px-1 pt-3 mb-2">
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{hasActiveFilters ? "RESULTS" : "SHOWING"}</span>
              <span className="text-sm text-muted-foreground">—</span>
              <span className="text-sm text-foreground">{totalCount} task{totalCount !== 1 ? "s" : ""}</span>
            </div>
            <div className="flex-1 overflow-auto">
              <Table className="table-zebra">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[30%]">Title</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Assignee</TableHead>
                    <TableHead>Labels</TableHead>
                    <TableHead>Animals</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead className="text-center">Info</TableHead>
                    <TableHead>Updated</TableHead>
                    {canEdit && <TableHead className="w-10"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tasksList.map(task => (
                    <TableRow
                      key={task.id}
                      className="cursor-pointer"
                      data-testid={`row-task-${task.id}`}
                      onClick={() => handleTaskClick(task.id)}
                    >
                      <TableCell className="font-medium" data-testid={`text-task-title-${task.id}`}>
                        {task.title}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" data-testid={`badge-status-${task.id}`}>
                          {task.statusName}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <PriorityBadge priority={task.priority} />
                      </TableCell>
                      <TableCell>
                        {task.assigneeUserId ? (
                          <div className="flex items-center gap-1.5">
                            <Avatar className="h-6 w-6">
                              {task.assigneeAvatarUrl && (
                                <AvatarImage src={getAvatarSrc(task.assigneeAvatarUrl, task.assigneeUserId)} alt={getDisplayName(task.assigneeFirstName, task.assigneeLastName, task.assigneeUsername)} />
                              )}
                              <AvatarFallback className="text-[10px]">
                                {getInitials(getDisplayName(task.assigneeFirstName, task.assigneeLastName, task.assigneeUsername), task.assigneeUsername)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm text-muted-foreground" data-testid={`text-assignee-${task.id}`}>
                              {getDisplayName(task.assigneeFirstName, task.assigneeLastName, task.assigneeUsername)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground" data-testid={`text-assignee-${task.id}`}>
                            Unassigned
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {task.labels.map(l => (
                            <Badge
                              key={l.labelId}
                              variant="outline"
                              className="text-xs"
                              data-testid={`badge-label-${task.id}-${l.labelId}`}
                            >
                              {l.labelColor && (
                                <span
                                  className="w-2 h-2 rounded-full mr-1 inline-block"
                                  style={{ backgroundColor: l.labelColor }}
                                />
                              )}
                              {l.labelName}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <LinkedAnimalsTooltip taskId={task.id} animalCount={task.animalCount}>
                          <span className="text-sm text-muted-foreground" data-testid={`text-animals-${task.id}`}>
                            {task.animalCount > 0 ? (
                              <span className="flex items-center gap-1">
                                <PawPrint className="w-3 h-3 shrink-0" />
                                {task.firstAnimalName || "Animal"}
                                {task.animalCount > 1 && <span className="text-xs">+{task.animalCount - 1}</span>}
                              </span>
                            ) : (
                              "None"
                            )}
                          </span>
                        </LinkedAnimalsTooltip>
                      </TableCell>
                      <TableCell>
                        {task.dueDate ? (
                          <span
                            className={isOverdue(task.dueDate) ? "text-destructive font-medium" : "text-muted-foreground"}
                            data-testid={`text-due-date-${task.id}`}
                          >
                            {new Date(task.dueDate + "T00:00:00").toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-muted-foreground text-sm" data-testid={`text-due-date-${task.id}`}>--</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2 text-muted-foreground text-xs">
                          {task.interactionCount > 0 && (
                            <span className="flex items-center gap-0.5" data-testid={`text-interactions-${task.id}`}>
                              <Link2 className="w-3 h-3" />{task.interactionCount}
                            </span>
                          )}
                          {task.checklist.total > 0 && (
                            <span className="flex items-center gap-0.5" data-testid={`text-checklist-${task.id}`}>
                              <CheckSquare className="w-3 h-3" />{task.checklist.done}/{task.checklist.total}
                            </span>
                          )}
                          {task.commentCount > 0 && (
                            <span className="flex items-center gap-0.5" data-testid={`text-comments-${task.id}`}>
                              <MessageSquare className="w-3 h-3" />{task.commentCount}
                            </span>
                          )}
                          {(task.attachmentCount ?? 0) > 0 && (
                            <span className="flex items-center gap-0.5" data-testid={`text-attachments-${task.id}`}>
                              <Paperclip className="w-3 h-3" />{task.attachmentCount}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground" data-testid={`text-updated-${task.id}`}>
                          {new Date(task.updatedAt).toLocaleDateString()}
                        </span>
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" data-testid={`button-actions-${task.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
                              <DropdownMenuItem
                                className="text-xs font-medium text-muted-foreground"
                                disabled
                              >
                                Change Status
                              </DropdownMenuItem>
                              {activeStatuses.filter(s => s.id !== task.statusId).map(s => (
                                <DropdownMenuItem
                                  key={s.id}
                                  data-testid={`action-status-${task.id}-${s.id}`}
                                  onClick={() => changeStatusMutation.mutate({ taskId: task.id, statusId: s.id })}
                                >
                                  {s.name}
                                </DropdownMenuItem>
                              ))}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-xs font-medium text-muted-foreground"
                                disabled
                              >
                                Assign To
                              </DropdownMenuItem>
                              {members.map(m => (
                                <DropdownMenuItem
                                  key={m.userId}
                                  data-testid={`action-assign-${task.id}-${m.userId}`}
                                  onClick={() => changeAssigneeMutation.mutate({ taskId: task.id, assigneeUserId: m.userId })}
                                >
                                  <UserCircle className="w-3 h-3 mr-1" />
                                  {getDisplayName(m.firstName, m.lastName, m.username)}
                                </DropdownMenuItem>
                              ))}
                              {task.assigneeUserId && (
                                <DropdownMenuItem
                                  data-testid={`action-unassign-${task.id}`}
                                  onClick={() => changeAssigneeMutation.mutate({ taskId: task.id, assigneeUserId: null })}
                                >
                                  <X className="w-3 h-3 mr-1" />
                                  Unassign
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                data-testid={`action-duplicate-${task.id}`}
                                onClick={() => handleDuplicate(task.id)}
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Duplicate
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                data-testid={`action-archive-${task.id}`}
                                onClick={() => archiveMutation.mutate(task.id)}
                                className="text-destructive"
                              >
                                <Archive className="w-3 h-3 mr-1" />
                                Archive
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              total={totalCount}
              pageSize={pageSize}
              startIndex={(page - 1) * pageSize + 1}
              endIndex={Math.min(page * pageSize, totalCount)}
              onPageChange={setPage}
              onPageSizeChange={size => { setPageSize(size); setPage(1); }}
              itemLabel="tasks"
              testIdPrefix="tasks"
              className="border-t"
            />
          </>
        )}
        </div>

        {selectedTaskId && !isMobile && (
          <div className="w-[440px] border-l border-border/40 shrink-0 overflow-y-auto bg-background shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]" data-testid="task-panel-container">
            <TaskPreviewPanel
              taskId={selectedTaskId}
              boardId={selectedBoardId}
              onClose={handlePanelClose}
            />
          </div>
        )}
      </div>

      {selectedBoardId && (
        <>
          <TemplatePicker
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            boardId={selectedBoardId}
            onSelect={handleTemplateSelect}
            boardType={currentBoard?.type}
          />
          <CreateTaskSheet
            open={createOpen}
            onOpenChange={(o) => {
              setCreateOpen(o);
              if (!o) {
                setCreateStatusId(null);
                setCreateTemplateId(null);
                setCreateTemplateTitle("");
                setCreateTemplateDescription("");
                setCreateTemplatePriority(null);
                setCreateTemplateLabelIds([]);
              }
            }}
            boardId={selectedBoardId}
            statuses={activeStatuses}
            labels={activeLabels}
            members={members}
            initialStatusId={createStatusId ?? undefined}
            initialTitle={createTemplateTitle}
            initialDescription={createTemplateDescription}
            initialPriority={createTemplatePriority}
            initialLabelIds={createTemplateLabelIds}
            templateId={createTemplateId ?? undefined}
            onCreated={(taskId) => handleTaskClick(taskId)}
          />
        </>
      )}
    </div>
  );
}
